/**
 * Règles de l'identité sonore (E§4) — logique pure, sans Howler ni WebAudio.
 *
 * Tout ce qui décide **quoi** jouer et **comment** vit ici, donc est testable sans
 * navigateur ni fichier son. `audio/sound.ts` ne fait plus qu'obéir.
 */

/**
 * Gamme pentatonique majeure, en demi-tons depuis la tonique.
 *
 * C'est le cœur de l'identité sonore : une pentatonique n'a pas de seconde mineure,
 * donc **deux notes quelconques sonnent juste ensemble**. Un idle superpose sans cesse
 * des sons non planifiés — c'est ce choix, et pas le mixage, qui fait la différence
 * entre un jeu soigné et un jeu bruyant.
 */
export const PENTATONIC_SEMITONES = [0, 2, 4, 7, 9] as const;

/** Rapport de fréquence d'un demi-ton. */
const SEMITONE_RATIO = 2 ** (1 / 12);

/** Convertit un écart en demi-tons en facteur de vitesse de lecture. */
export function semitoneToRate(semitones: number): number {
  return SEMITONE_RATIO ** semitones;
}

/**
 * Demi-ton de la n-ième note de la gamme, en repartant à l'octave au-delà de cinq.
 * Permet de monter indéfiniment sans jamais sortir de la palette.
 */
export function pentatonicSemitone(step: number): number {
  const size = PENTATONIC_SEMITONES.length;
  const octave = Math.floor(step / size);
  const index = ((step % size) + size) % size;
  return (PENTATONIC_SEMITONES[index] ?? 0) + octave * 12;
}

/** Variation de hauteur appliquée à chaque son, pour casser la répétition mécanique. */
export const PITCH_JITTER = 0.03;

/**
 * Applique ±3 % de hauteur (E§4). `random` est injecté : sans cela la fonction ne
 * serait pas testable, et un test qui ne vérifie pas les bornes ne sert à rien.
 */
export function jitterRate(rate: number, random: () => number = Math.random): number {
  return rate * (1 + (random() * 2 - 1) * PITCH_JITTER);
}

/**
 * Escalade du carillon à la pose (E§1.1).
 *
 * Chaque pose consécutive monte d'un degré de la gamme ; trois secondes d'inactivité
 * remettent à zéro. Poser cinq bâtiments d'affilée joue une gamme montante — c'est le
 * détail que les joueurs remarquent sans savoir pourquoi ils y reviennent.
 */
export const CHIME_RESET_MS = 3000;

export class ChimeLadder {
  private step = 0;
  private lastAt = Number.NEGATIVE_INFINITY;

  /** Rend le demi-ton à jouer maintenant, et avance l'échelle. */
  next(now: number): number {
    this.step = now - this.lastAt > CHIME_RESET_MS ? 0 : this.step + 1;
    this.lastAt = now;
    return pentatonicSemitone(this.step);
  }

  reset(): void {
    this.step = 0;
    this.lastAt = Number.NEGATIVE_INFINITY;
  }

  get currentStep(): number {
    return this.step;
  }
}

/** Décision rendue par le limiteur de simultanéité. */
export interface SoundDecision {
  readonly play: boolean;
  /** Multiplicateur de volume : un son unique remplace une grappe, donc plus fort. */
  readonly gain: number;
}

export const LIMITER_WINDOW_MS = 200;
export const LIMITER_MAX_IN_WINDOW = 4;

/**
 * Limiteur de simultanéité (E§4) : au maximum quatre sons du même type par fenêtre de
 * 200 ms. Au-delà, on n'en joue qu'un, plus fort.
 *
 * Sans cela, dix pièces qui arrivent ensemble produisent un craquement au lieu d'un
 * tintement — et c'est pire que le silence.
 */
export class SoundLimiter {
  private readonly history = new Map<string, number[]>();

  decide(type: string, now: number): SoundDecision {
    const times = this.history.get(type) ?? [];

    // Compactage sur place : la fenêtre glissante ne doit pas allouer à chaque son.
    let write = 0;
    // Boucle indexée assumée : compactage sur place, l'index d'écriture recule.
    // eslint-disable-next-line @typescript-eslint/prefer-for-of
    for (let i = 0; i < times.length; i++) {
      const at = times[i];
      if (at !== undefined && now - at < LIMITER_WINDOW_MS) {
        times[write++] = at;
      }
    }
    times.length = write;

    if (times.length >= LIMITER_MAX_IN_WINDOW) {
      return { play: false, gain: 0 };
    }

    times.push(now);
    this.history.set(type, times);

    // Plus la grappe est dense, plus le son porte : la grappe reste perceptible
    // même quand on en tait une partie.
    return { play: true, gain: 1 + times.length * 0.08 };
  }

  clear(): void {
    this.history.clear();
  }
}
